import {
  useAdminDoctolibMotifs,
  useAdminDoctolibSpecialites,
  useAdminDoctolibSynchronisations,
  type ImportateurDoctolib,
  type MotifDoctolibCatalogue,
  type SpecialiteDoctolib,
  type SynchronisationDoctolib,
} from '@fidalia/api';
import {
  Alert,
  Avatar,
  Badge,
  DataTable,
  Input,
  PageHeader,
  Pagination,
  Select,
  TabPanel,
  Tabs,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { MdLabel, MdMedicalServices, MdOpenInNew, MdSearch, MdSync, MdTv } from 'react-icons/md';

import { errorMessage } from '@/lib/auth';
import { useDebounced } from '@/lib/use-debounced';

const PAGE_SIZE = 50;

type Onglet = 'synchronisations' | 'specialites' | 'motifs';

export const Route = createFileRoute('/_app/doctolib')({
  component: DoctolibPage,
});

/** « il y a 3 jours », « aujourd'hui » : suffit à situer une entrée du catalogue. */
function ilYA(date: string): string {
  const jours = Math.floor((Date.now() - new Date(date).getTime()) / 86_400_000);
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return 'hier';
  if (jours < 30) return `il y a ${jours} jours`;
  const mois = Math.floor(jours / 30);
  return mois === 1 ? 'il y a 1 mois' : `il y a ${mois} mois`;
}

/**
 * Teinte Doctolib d'un motif, en pastille devant son nom.
 *
 * Décorative et sans garantie de contraste — ces couleurs sont choisies par
 * le cabinet pour des blocs d'agenda, pas pour une interface — d'où le
 * cerclage, qui détoure les teintes les plus claires sur fond blanc.
 */
function Pastille({ couleur }: { couleur: string | null }) {
  if (!couleur) return <span aria-hidden="true" className="size-2.5 shrink-0" />;
  return (
    <span
      aria-hidden="true"
      style={{ backgroundColor: couleur }}
      className="size-2.5 shrink-0 rounded-full ring-1 ring-inset ring-content/15"
    />
  );
}

/** Membre dont la dernière synchronisation a écrit cette entrée du catalogue. */
function Importateur({ importateur }: { importateur: ImportateurDoctolib | null }) {
  if (!importateur) return <span className="text-content-subtle">—</span>;
  const nom = `${importateur.prenom} ${importateur.nom}`.trim();
  return (
    <div className="flex min-w-0 flex-col">
      <span className="truncate">{nom}</span>
      <span className="truncate text-xs text-content-muted">{importateur.email}</span>
    </div>
  );
}

/**
 * Catalogue Doctolib, alimenté par chaque synchronisation de cabinet.
 *
 * Append-only côté backend (voir `UpsertCatalogueDoctolibUseCase`) : une
 * spécialité ou un motif qui n'est plus déclaré par aucun cabinet reste
 * listé ici, avec sa dernière date de vue. C'est la mémoire de ce que
 * Fidalia a croisé chez Doctolib, pas une photo de l'instant.
 */
function DoctolibPage() {
  const [onglet, setOnglet] = useState<Onglet>('synchronisations');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  // Motifs seulement : `undefined` = toutes spécialités confondues.
  const [specialiteFiltre, setSpecialiteFiltre] = useState<number | undefined>(undefined);

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const changeSpecialiteFiltre = (value: string) => {
    setSpecialiteFiltre(value ? Number(value) : undefined);
    setPage(1);
  };

  const changeOnglet = (value: Onglet) => {
    setOnglet(value);
    setPage(1);
    setSearch('');
    setSpecialiteFiltre(undefined);
  };

  const synchronisations = useAdminDoctolibSynchronisations({
    page,
    limit: PAGE_SIZE,
    search: onglet === 'synchronisations' ? debouncedSearch : '',
  });
  const specialites = useAdminDoctolibSpecialites({
    page,
    limit: PAGE_SIZE,
    search: onglet === 'specialites' ? debouncedSearch : '',
  });
  const motifs = useAdminDoctolibMotifs({
    page,
    limit: PAGE_SIZE,
    search: onglet === 'motifs' ? debouncedSearch : '',
    specialiteId: specialiteFiltre,
  });
  // Toutes les spécialités connues, pour peupler le filtre des motifs.
  const toutesSpecialites = useAdminDoctolibSpecialites({ limit: 200 });

  const colonnesSynchronisations: TableColumn<SynchronisationDoctolib>[] = [
    {
      key: 'compte',
      title: 'Compte Fidalia',
      icon: MdSync,
      cell: (row) => {
        const nom = `${row.prenom} ${row.nom}`.trim();
        return (
          <div className="flex items-center gap-3">
            <Avatar name={nom} size="sm" tone="auto" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{nom}</span>
              <span className="truncate text-xs text-content-muted">{row.email}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'cabinet',
      title: 'Cabinet',
      cell: (row) => <span className="text-content-muted">{row.cabinetNom}</span>,
    },
    {
      key: 'doctolib',
      title: 'Compte Doctolib',
      cell: (row) => {
        const contenu = (
          <>
            <span className="flex items-center gap-1 truncate font-medium">
              {row.doctolibNom ?? '—'}
              {row.lienDoctolib ? (
                <MdOpenInNew aria-hidden="true" className="size-3.5 shrink-0 text-content-subtle" />
              ) : null}
            </span>
            <span className="truncate text-xs text-content-muted">{row.doctolibEmail ?? '—'}</span>
          </>
        );

        // Cliquable seulement quand le lien a pu être reconstruit (voir
        // `lienDoctolibDuCabinet` côté backend) : sans nom et ville
        // exploitables sur la fiche Doctolib, il n'y a nulle part où mener.
        if (!row.lienDoctolib) return <div className="flex min-w-0 flex-col">{contenu}</div>;

        return (
          <a
            href={row.lienDoctolib}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="flex min-w-0 flex-col hover:underline"
            title="Ouvrir la fiche du cabinet sur Doctolib"
          >
            {contenu}
          </a>
        );
      },
    },
    {
      key: 'syncAt',
      title: 'Dernière synchro',
      cell: (row) => <span className="text-content-muted">{ilYA(row.doctolibSyncAt)}</span>,
    },
  ];

  const colonnesSpecialites: TableColumn<SpecialiteDoctolib>[] = [
    {
      key: 'id',
      title: 'ID',
      className: 'w-px',
      cell: (row) => <span className="text-content-muted">{row.id}</span>,
    },
    {
      key: 'nom',
      title: 'Nom',
      icon: MdMedicalServices,
      cell: (row) => <span className="font-medium">{row.nom}</span>,
    },
    {
      key: 'slug',
      title: 'Slug',
      cell: (row) => <span className="text-content-muted">{row.slug ?? '—'}</span>,
    },
    {
      key: 'genre',
      title: 'Kind',
      cell: (row) =>
        row.genre ? (
          <Badge tone="neutral">{row.genre}</Badge>
        ) : (
          <span className="text-content-subtle">—</span>
        ),
    },
    {
      key: 'groupe',
      title: 'Data sub group',
      cell: (row) => <span className="text-content-muted">{row.groupe ?? '—'}</span>,
    },
    {
      key: 'rappelAdapte',
      title: 'Recall friendly',
      cell: (row) => (
        <Badge tone={row.rappelAdapte ? 'success' : 'neutral'}>
          {row.rappelAdapte ? 'Oui' : 'Non'}
        </Badge>
      ),
    },
    {
      key: 'cabinets',
      title: 'Cabinets',
      align: 'right',
      cell: (row) => (
        <Badge tone={row.cabinetsCount > 0 ? 'success' : 'neutral'}>{row.cabinetsCount}</Badge>
      ),
    },
    {
      key: 'vue',
      title: 'Dernière synchro',
      cell: (row) => <span className="text-content-muted">{ilYA(row.derniereVueLe)}</span>,
    },
    {
      key: 'import',
      title: 'Dernière importation par',
      cell: (row) => <Importateur importateur={row.dernierImportPar} />,
    },
  ];

  const colonnesMotifs: TableColumn<MotifDoctolibCatalogue>[] = [
    {
      key: 'nom',
      title: 'Motif',
      icon: MdLabel,
      cell: (row) => (
        <span className="flex min-w-0 items-center gap-2">
          <Pastille couleur={row.couleur} />
          <span className="truncate font-medium">{row.nom}</span>
        </span>
      ),
    },
    {
      key: 'doctolibId',
      title: 'ID Doctolib',
      cell: (row) => (
        <span className="text-content-muted">{row.doctolibId !== null ? row.doctolibId : '—'}</span>
      ),
    },
    {
      key: 'premiere',
      title: 'Vu pour la première fois',
      cell: (row) => <span className="text-content-muted">{ilYA(row.premiereVueLe)}</span>,
    },
    {
      key: 'derniere',
      title: 'Dernière synchro',
      cell: (row) => <span className="text-content-muted">{ilYA(row.derniereVueLe)}</span>,
    },
    {
      key: 'import',
      title: 'Dernière importation par',
      cell: (row) => <Importateur importateur={row.dernierImportPar} />,
    },
  ];

  const donnees =
    onglet === 'synchronisations'
      ? synchronisations
      : onglet === 'specialites'
        ? specialites
        : motifs;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdTv />}
        title="Données Doctolib"
        description="Spécialités et motifs de consultation croisés chez Doctolib, tous cabinets confondus. Jamais purgé : une entrée qui disparaît d'un cabinet reste ici."
      />

      {donnees.error ? (
        <Alert tone="danger">{errorMessage(donnees.error, 'Chargement impossible.')}</Alert>
      ) : null}

      <Tabs
        id="doctolib-catalogue"
        aria-label="Catalogue Doctolib"
        value={onglet}
        onChange={changeOnglet}
        items={[
          {
            value: 'synchronisations',
            label: 'Synchronisations',
            icon: <MdSync />,
            badge: synchronisations.data?.total,
          },
          { value: 'motifs', label: 'Motifs', icon: <MdLabel />, badge: motifs.data?.total },
          {
            value: 'specialites',
            label: 'Spécialités',
            icon: <MdMedicalServices />,
            badge: specialites.data?.total,
          },
        ]}
      />

      <TabPanel id="doctolib-catalogue" value="synchronisations" active={onglet}>
        <div className="flex flex-col gap-4">
          <Input
            type="search"
            aria-label="Rechercher une synchronisation"
            placeholder="Nom, email, cabinet…"
            icon={<MdSearch className="size-6" />}
            containerClassName="h-11 w-full"
            value={search}
            onChange={(event) => changeSearch(event.target.value)}
          />

          <DataTable
            mobileCards
            data={synchronisations.data?.items ?? []}
            columns={colonnesSynchronisations}
            isLoading={synchronisations.isPending}
            skeletonRows={10}
            rowId={(row) => row.userId}
            emptyMessage={
              debouncedSearch
                ? `Aucun résultat pour « ${debouncedSearch} ».`
                : 'Aucune synchronisation pour l’instant.'
            }
          />

          {synchronisations.data ? (
            <Pagination
              page={synchronisations.data.page}
              total={synchronisations.data.total}
              limit={synchronisations.data.limit}
              onChange={setPage}
              label="synchronisations"
            />
          ) : null}
        </div>
      </TabPanel>

      <TabPanel id="doctolib-catalogue" value="motifs" active={onglet}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              type="search"
              aria-label="Rechercher un motif"
              placeholder="Rechercher un motif…"
              icon={<MdSearch className="size-6" />}
              containerClassName="h-11 w-full sm:min-w-64 sm:flex-1"
              value={search}
              onChange={(event) => changeSearch(event.target.value)}
            />

            <Select
              aria-label="Filtrer par spécialité"
              containerClassName="h-11 w-full sm:w-56"
              value={specialiteFiltre !== undefined ? String(specialiteFiltre) : ''}
              onChange={(event) => changeSpecialiteFiltre(event.target.value)}
            >
              <option value="">Toutes spécialités</option>
              {(toutesSpecialites.data?.items ?? []).map((specialite) => (
                <option key={specialite.id} value={specialite.id}>
                  {specialite.nom}
                </option>
              ))}
            </Select>
          </div>

          <DataTable
            mobileCards
            data={motifs.data?.items ?? []}
            columns={colonnesMotifs}
            isLoading={motifs.isPending}
            skeletonRows={10}
            rowId={(row) => row.id}
            emptyMessage={
              debouncedSearch
                ? `Aucun résultat pour « ${debouncedSearch} ».`
                : 'Aucun motif relevé pour l’instant.'
            }
          />

          {motifs.data ? (
            <Pagination
              page={motifs.data.page}
              total={motifs.data.total}
              limit={motifs.data.limit}
              onChange={setPage}
              label="motifs"
            />
          ) : null}
        </div>
      </TabPanel>

      <TabPanel id="doctolib-catalogue" value="specialites" active={onglet}>
        <div className="flex flex-col gap-4">
          <Input
            type="search"
            aria-label="Rechercher une spécialité"
            placeholder="Rechercher une spécialité…"
            icon={<MdSearch className="size-6" />}
            containerClassName="h-11 w-full"
            value={search}
            onChange={(event) => changeSearch(event.target.value)}
          />

          <DataTable
            mobileCards
            data={specialites.data?.items ?? []}
            columns={colonnesSpecialites}
            isLoading={specialites.isPending}
            skeletonRows={10}
            rowId={(row) => String(row.id)}
            emptyMessage={
              debouncedSearch
                ? `Aucun résultat pour « ${debouncedSearch} ».`
                : 'Aucune spécialité relevée pour l’instant.'
            }
          />

          {specialites.data ? (
            <Pagination
              page={specialites.data.page}
              total={specialites.data.total}
              limit={specialites.data.limit}
              onChange={setPage}
              label="spécialités"
            />
          ) : null}
        </div>
      </TabPanel>
    </div>
  );
}
